import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  TextInput,
  ListProps,
  useRefresh,
  useNotify,
  Button,
  FunctionField,
  SelectInput,
} from 'react-admin';
import { Box, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import useAuthorize from '../hooks/useAuthorize';
import { UserRole } from '../types/types';

const ApproveButton = ({ record }: { record: any }) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);

  if (!record || record.status !== 'pending') return null;
  if (record.source === 'website_wizard' || record.seller_category === 'marketplace') return null;

  const handleApprove = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('user_authToken');
      const baseUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${baseUrl}/sellerRegistrationRequests/${record.id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to approve');
      notify('Seller approved successfully', { type: 'success' });
      refresh();
    } catch (e: any) {
      notify(e.message || 'Failed to approve', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      label="Approve"
      onClick={handleApprove}
      disabled={loading}
      startIcon={<CheckCircleIcon />}
      color="success"
      size="small"
    />
  );
};

const RejectButton = ({ record }: { record: any }) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);

  if (!record || record.status !== 'pending') return null;

  const handleReject = async () => {
    if (!window.confirm('Reject this seller registration request?')) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('user_authToken');
      const baseUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${baseUrl}/sellerRegistrationRequests/${record.id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reject');
      notify('Request rejected', { type: 'success' });
      refresh();
    } catch (e: any) {
      notify(e.message || 'Failed to reject', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      label="Reject"
      onClick={handleReject}
      disabled={loading}
      startIcon={<CancelIcon />}
      color="error"
      size="small"
    />
  );
};

const StatusField = ({ record }: { record: any }) => {
  if (!record) return null;
  const status = record.status || 'pending';
  const color = status === 'approved' ? 'success' : status === 'rejected' ? 'error' : 'warning';
  return <Chip label={status} color={color} size="small" />;
};

const filters = [
  <SelectInput
    label="Status"
    source="status"
    choices={[
      { id: 'pending', name: 'Pending' },
      { id: 'approved', name: 'Approved' },
      { id: 'rejected', name: 'Rejected' },
    ]}
    resettable
    key="status"
  />,
  <TextInput label="First Name" source="first_name" resettable alwaysOn key="first_name" />,
  <TextInput label="Company" source="company_name" resettable key="company_name" />,
  <TextInput label="Email" source="email_address" resettable key="email_address" />,
  <TextInput label="Phone" source="phone_mobile" resettable key="phone_mobile" />,
];

export const SellerRegistrationRequestList: React.FC<ListProps> = (props) => {
  useAuthorize([UserRole.Admin, UserRole.ProductAdmin]);

  return (
    <List
      {...props}
      filters={filters}
      filterDefaultValues={{ status: 'pending' }}
      sort={{ field: 'created_at', order: 'DESC' }}
    >
      <Datagrid bulkActionButtons={false}>
        <TextField source="id" label="ID" />
        <TextField source="first_name" label="First Name" />
        <TextField source="last_name" label="Last Name" />
        <TextField source="company_name" label="Company" />
        <TextField source="email_address" label="Email" />
        <TextField source="phone_mobile" label="Phone" />
        <TextField source="source" label="Source" />
        <TextField source="seller_category" label="Seller type" />
        <FunctionField label="Status" render={(r: any) => <StatusField record={r} />} />
        <TextField source="created_at" label="Requested At" />
        <FunctionField
          label="Actions"
          render={(record: any) => (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <ApproveButton record={record} />
              <RejectButton record={record} />
            </Box>
          )}
        />
      </Datagrid>
    </List>
  );
};

export default SellerRegistrationRequestList;
